import type { MouseEventHandler } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { OfferPageFilterType, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import moment from 'moment'
import TextButton from 'LuxKit/Button/TextButton'
import { useAppSelector } from 'hooks/reduxHooks'
import Group from 'components/utils/Group'
import LineShoppingCartIcon from 'LuxKit/Icons/line/LineShoppingCartIcon'
import { formatSaleUnitCount } from 'hooks/useSaleUnitFormatting'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getSource } from 'selectors/offerSelectors'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import ModalContext from 'contexts/ModalContext'
import { SOURCE_TYPES } from 'constants/source'
import { getRelatedPayAtPropertyRate } from '../utils'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import BedbankPayAtPropertyModal from './Modals/BedbankPayAtPropertyModal'
import { offerFlightsEnabled } from 'selectors/offerPage/offerPageSelectors'
import { useMetaOffersDiscount } from 'contexts/MetaOffersDiscountContext'

interface Props {
  rate: App.BedbankRate
  payAtPropertyRates: Array<App.BedbankRate>
  rooms: Array<OfferPageRoom>
  offer: App.BedbankOffer | App.BedbankOfferSummary
  flightOrigin?: App.Airport
  flightPrice?: number
  checkIn?: string
  departureTooEarly?: boolean
  flightsEnabled?: boolean
  ratePosition: number
  isFlightsSelected: boolean
  isLoggedIn: boolean
  onBookNow?: (
    rate: App.BedbankRate,
    ratePosition: number,
    flights?: boolean,
    keepBrowsing?: boolean,
    isInsuranceSelected?: boolean,
    discount?: number,
  ) => void
  onAddToUserCartNow?: (
    rate: App.BedbankRate,
    ratePosition: number,
    flights?: boolean,
    isInsuranceSelected?: boolean,
  ) => void
  isInsuranceSelected?: boolean
}

function BedbankPackageTileCTAs(props: Props) {
  const {
    rate,
    payAtPropertyRates,
    rooms,
    offer,
    checkIn,
    flightOrigin,
    flightPrice,
    departureTooEarly,
    flightsEnabled,
    ratePosition,
    onBookNow,
    onAddToUserCartNow,
    isFlightsSelected,
    isLoggedIn,
    isInsuranceSelected,
  } = props

  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const metaOffersDiscount = useMetaOffersDiscount()
  const offerPageDispatch = useOfferPageDispatch()
  const hasFlights = !!flightPrice
  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterType })
  }, [offerPageDispatch])

  const departureTooFarInFuture = useMemo(() => {
    if (checkIn) {
      return moment(checkIn).diff(moment.utc(), 'days') > 330
    }
    return false
  }, [checkIn])

  const hasDates = !!checkIn

  const canAddToUserCart = hasDates

  const isPayAtPropertyFeatureEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.croPayAtProperty)

  // Use meta offers discount if available, otherwise fall back to rate discount
  const metaOffersDiscountPackage = metaOffersDiscount?.packages?.find(pkg => pkg.fkRoomTypeId === rate.room.roomId && pkg.rateId === rate.id)

  const source = useAppSelector(state => getSource(state))

  const relatedPayAtPropertyRate = useMemo(() => getRelatedPayAtPropertyRate(payAtPropertyRates, rate), [payAtPropertyRates, rate])

  const getChosenRate = useCallback(async(hasFlight: boolean): Promise<App.BedbankRate | undefined> => {
    if (!hasFlight && isPayAtPropertyFeatureEnabled) {
      const propertyCountryCode = offer.property.address.countryCode

      if (relatedPayAtPropertyRate) {
        const chosenRate = await showModal<App.BedbankRate | undefined>(<BedbankPayAtPropertyModal payAtPropertyRate={relatedPayAtPropertyRate} lePaymentNowRate={rate} propertyCountryCode={propertyCountryCode} />)
        return chosenRate
      }
    }

    return rate
  }, [relatedPayAtPropertyRate, rate, isPayAtPropertyFeatureEnabled, showModal, offer.property.address.countryCode])

  const onBookNowHandler = useMemo<MouseEventHandler<HTMLButtonElement> | undefined>(() => {
    if (!onBookNow) { return undefined }
    return async(event) => {
      const keepBrowsing: boolean = !!event.currentTarget.dataset.keepBrowsing
      const hasFlight: boolean = !!event.currentTarget.dataset.hasFlight

      const chosenRate = await getChosenRate(hasFlight)
      if (!chosenRate) {
        return
      }

      onBookNow(chosenRate, ratePosition, hasFlight, keepBrowsing, isInsuranceSelected, metaOffersDiscountPackage?.discount)
    }
  }, [onBookNow, getChosenRate, ratePosition, isInsuranceSelected, metaOffersDiscountPackage?.discount])

  const onAddToUserCartHandler = useMemo<MouseEventHandler<HTMLButtonElement> | undefined>(() => {
    if (!onAddToUserCartNow) { return undefined }
    return async(event) => {
      const hasFlight: boolean = !!event.currentTarget.dataset.hasFlight

      const chosenRate = await getChosenRate(hasFlight)
      if (!chosenRate) {
        return
      }

      onAddToUserCartNow(chosenRate, ratePosition, hasFlight, isInsuranceSelected)
    }
  }, [onAddToUserCartNow, getChosenRate, ratePosition, isInsuranceSelected])

  const isMetaSearch = useMemo(() => {
    return source?.includes(SOURCE_TYPES.METASEARCH)
  }, [source])

  const onLoginForMemberSavings = useCallback(async() => {
    showModal<AccountAccessModalResult>(<AccountAccessModal />)
  }, [showModal])

  const shouldDisplayLoginForMemberSavings = !isLoggedIn && isMetaSearch

  const flightsBookingActive = flightsEnabled && flightOrigin && isFlightsSelected

  const checkAvailabilityText = intl.formatMessage({
    id: 'Hp693KJ5TWYZTX6Da5lu',
    defaultMessage: 'Check availability',
    description: 'Check availability button text for bedbank package tile when dates not selected',
  })

  const bookNowText = intl.formatMessage({
    id: 'q7o047LRZlbUoW6hMWNQ',
    defaultMessage: 'Book now',
    description: 'Book now button text for bedbank package tile pricing',
  })

  const hotelOnlyBookNowText = useMemo(() => {
    if (rooms.length > 1) {
      return intl.formatMessage(
        {
          id: 'GJkW8x0u1wbk52WQxU_V',
          defaultMessage: 'Book {saleUnitLabel}',
          description: 'Book button text with room count for bedbank package tile pricing',
        },
        { saleUnitLabel: formatSaleUnitCount(intl, offer.saleUnit, rooms.length) },
      )
    }
    return intl.formatMessage({
      id: 'q7o047LRZlbUoW6hMWNQ',
      defaultMessage: 'Book now',
      description: 'Book now button text for bedbank package tile pricing',
    })
  }, [intl, rooms.length, offer.saleUnit])

  const bookNowButtonText = hasDates ? hotelOnlyBookNowText : checkAvailabilityText
  const flightsBookNowButtonText = hasDates ? bookNowText : checkAvailabilityText

  if (metaOffersDiscountPackage) {
    if (flightsBookingActive) {
      return <Group direction="vertical" gap={8} noDisplayWhenEmpty>
        {flightOrigin.code !== offer.flights?.airportCode && !departureTooFarInFuture && !departureTooEarly && <>
          {onBookNowHandler && <TextButton
            size="medium"
            kind="primary"
            data-testid="book-button"
            data-has-flight
            onClick={onBookNowHandler}
          >
            {flightsBookNowButtonText}
          </TextButton>}
        </>}
        {!hasFlights && flightOrigin.code === offer.flights?.airportCode && <TextButton
          size="medium"
          data-filter="airport"
          kind="tertiary"
          onClick={onToggleFilter}
        >
          {intl.formatMessage({
            id: 'DNH4VOXFxbRSkbJQbpov',
            defaultMessage: 'Change airport',
            description: 'Change airport button text for bedbank flights pricing',
          })}
        </TextButton>}
        {flightOrigin.code !== offer.flights?.airportCode && (departureTooEarly || departureTooFarInFuture) && <TextButton
          size="medium"
          data-filter="checkin"
          kind="tertiary"
          onClick={onToggleFilter}
        >
          {intl.formatMessage({
            id: '2DaQ-5UIMDaCnZ8LdGFM',
            defaultMessage: 'Change departure date',
            description: 'Change departure date button text for bedbank flights pricing',
          })}
        </TextButton>}
      </Group>
    } else {
      return <Group direction="vertical" gap={8} noDisplayWhenEmpty>
        {shouldDisplayLoginForMemberSavings && <TextButton size="large" kind="primary" nonInteractable onClick={onLoginForMemberSavings}>
          {intl.formatMessage({
            id: '8sH95fSbeixEune80fGe',
            defaultMessage: 'Sign up & save more',
            description: 'Sign up button text to show member savings for bedbank package tile',
          })}
        </TextButton>}
        {!shouldDisplayLoginForMemberSavings && onBookNowHandler && <TextButton
          size="medium"
          kind="primary"
          data-testid="book-button"
          onClick={onBookNowHandler}
        >
          {bookNowButtonText}
        </TextButton>}
      </Group>
    }
  } else {
    if (flightsBookingActive) {
      return <Group direction="vertical" gap={8} noDisplayWhenEmpty>
        {flightOrigin.code !== offer.flights?.airportCode && !departureTooFarInFuture && !departureTooEarly && <>
          {!canAddToUserCart && onBookNowHandler && <TextButton
            size="medium"
            kind="primary"
            data-testid="book-button"
            data-has-flight
            onClick={onBookNowHandler}
          >
            {flightsBookNowButtonText}
          </TextButton>}
          {canAddToUserCart && onAddToUserCartHandler && <TextButton
            size="medium"
            data-has-flight
            data-testid="book-button"
            onClick={onAddToUserCartHandler}
            kind="primary"
          >
            {intl.formatMessage({
              id: 'PkeYTTSihyUEth-8Cv5j',
              defaultMessage: 'Select package',
              description: 'Select package button text for bedbank flights pricing',
            })}
          </TextButton>}
        </>}
        {!hasFlights && flightOrigin.code === offer.flights?.airportCode && <TextButton
          size="medium"
          data-filter="airport"
          kind="tertiary"
          onClick={onToggleFilter}
        >
          {intl.formatMessage({
            id: 'DNH4VOXFxbRSkbJQbpov',
            defaultMessage: 'Change airport',
            description: 'Change airport button text for bedbank flights pricing',
          })}
        </TextButton>}
        {flightOrigin.code !== offer.flights?.airportCode && (departureTooEarly || departureTooFarInFuture) && <TextButton
          size="medium"
          data-filter="checkin"
          kind="tertiary"
          onClick={onToggleFilter}
        >
          {intl.formatMessage({
            id: '2DaQ-5UIMDaCnZ8LdGFM',
            defaultMessage: 'Change departure date',
            description: 'Change departure date button text for bedbank flights pricing',
          })}
        </TextButton>}
      </Group>
    } else {
      return <Group direction="vertical" gap={8} noDisplayWhenEmpty>
        {shouldDisplayLoginForMemberSavings && <TextButton size="large" kind="primary" nonInteractable onClick={onLoginForMemberSavings}>
          {intl.formatMessage({
            id: '8sH95fSbeixEune80fGe',
            defaultMessage: 'Sign up & save more',
            description: 'Sign up button text to show member savings for bedbank package tile',
          })}
        </TextButton>}
        {!shouldDisplayLoginForMemberSavings && onBookNowHandler && <TextButton
          size="medium"
          kind="primary"
          data-testid="book-button"
          onClick={onBookNowHandler}
        >
          {hasDates ? hotelOnlyBookNowText : checkAvailabilityText}
        </TextButton>}
        {canAddToUserCart && onAddToUserCartHandler && (
          <TextButton
            size="medium"
            onClick={onAddToUserCartHandler}
            startIcon={<LineShoppingCartIcon />}
            kind="secondary"
          >
            {intl.formatMessage({
              id: 'g0EvhYoaCd_EaQLo44w7',
              defaultMessage: 'Add to cart',
              description: 'Add to cart button text for bedbank package tile on the offer detail page',
            })}
          </TextButton>
        )}
      </Group>
    }
  }
}

type MappedProps = Pick<Props,
  | 'isLoggedIn'
  | 'isFlightsSelected'
  | 'checkIn'
  | 'flightsEnabled'
  | 'flightOrigin'
  | 'offer'
  | 'rooms'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  isLoggedIn: !!appState.auth.account.memberId,
  isFlightsSelected: state.flights,
  checkIn: state.checkInDate,
  flightsEnabled: offerFlightsEnabled(state, appState),
  flightOrigin: state.airport,
  offer: state.bedbankOffer!,
  rooms: state.rooms,
}), BedbankPackageTileCTAs)
